import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PLACEHOLDER_OPTIONS } from "@/lib/message-placeholders";

interface InsertPlaceholderDropdownProps {
  // Called with the chosen token (e.g. "{{member_first_name}}"). The consumer
  // owns insertion-at-cursor so the same dropdown works against any textarea.
  onInsert: (token: string) => void;
  disabled?: boolean;
  align?: "start" | "center" | "end";
  // Consumer-specific tokens appended after the shared catalog — e.g. the
  // Messaging Rule form adds one "{{booking_request_link:<id>}}" entry per
  // booking-request template. Other consumers (Canned Responses, Broadcast)
  // omit this and see the shared list only.
  extraOptions?: { label: string; token: string }[];
}

// Reusable "Insert dynamic placeholder" dropdown shared by Canned Responses,
// the Broadcast modal, and the Messaging Rule form. modal={true} keeps it from
// flashing closed when rendered inside a Radix Dialog/Sheet.
export function InsertPlaceholderDropdown({
  onInsert,
  disabled,
  align = "end",
  extraOptions,
}: InsertPlaceholderDropdownProps) {
  const options = extraOptions?.length
    ? [...PLACEHOLDER_OPTIONS, ...extraOptions]
    : PLACEHOLDER_OPTIONS;
  return (
    <DropdownMenu modal={true}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs px-2"
          disabled={disabled}
          data-testid="button-insert-placeholder"
        >
          Insert dynamic placeholder
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {options.map(({ label, token }) => (
          <DropdownMenuItem
            key={token}
            onSelect={() => onInsert(token)}
            data-testid={`menu-item-placeholder-${token}`}
          >
            <span>{label}</span>
            <code className="ml-2 text-xs text-gray-400 font-mono">{token}</code>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
